import React from 'react';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import CameraController from '../shared3d/CameraController';
import { DashedLine, Label, AxisLabel, ThickLine } from '../shared3d/LineRenderers';
import CargoBox from '../shared3d/CargoBox';

// Type definitions for component props
interface CogSceneProps {
    dimensions: {
        width: number;
        height: number;
        depth: number;
        scale?: number;
    };
    cog?: {
        width: number;
        length: number;
        height: number;
    };
    theme?: 'light' | 'dark';
    zoom?: number;
}

const CogPrism: React.FC<CogSceneProps> = ({ dimensions, cog, theme = 'dark' }) => {
    const { width, height, depth } = dimensions;

    const halfL = width / 2;
    const halfH = height / 2;
    const halfW = depth / 2;

    const center: [number, number, number] = cog
        ? [
            halfL - cog.length,
            -halfH + cog.height,
            halfW - cog.width,
        ]
        : [0, 0, 0];

    const baseCenter: [number, number, number] = [center[0], -halfH, center[2]];

    const normX = halfL > 0 ? Math.abs(center[0]) / halfL : 0;
    const normZ = halfW > 0 ? Math.abs(center[2]) / halfW : 0;

    const heightRisk = cog && height > 0
        ? (cog.height <= height / 2
            ? 0
            : Math.min(1, (cog.height - height / 2) / (height / 2 || 1)))
        : 0;

    const maxOffset = Math.max(normX, normZ, heightRisk);

    let cogColor = '#16a34a';
    if (maxOffset >= 0.6) {
        cogColor = '#ff1f1f';
    } else if (maxOffset >= 0.3) {
        cogColor = '#f97316';
    }

    const bluePoint: [number, number, number] = [center[0], -halfH, halfW];
    const greenPoint: [number, number, number] = [halfL, -halfH, center[2]];

    const axisLength = Math.max(width, height, depth) * 0.65;
    const isDark = theme === 'dark';
    const axisColorX = isDark ? '#f97316' : '#ea580c';
    const axisColorY = isDark ? '#4ade80' : '#15803d';
    const axisColorZ = isDark ? '#60a5fa' : '#1d4ed8';

    const axisLineWidth = isDark ? 4.0 : 5.0;
    const projectionLineWidth = 0.015; // World units for ThickLine

    return (
        <group>
            <DashedLine points={[new THREE.Vector3(0, 0, 0), new THREE.Vector3(axisLength, 0, 0)]} color={axisColorX} lineWidth={axisLineWidth} dashSize={0.15} gapSize={0.05} />
            <DashedLine points={[new THREE.Vector3(-axisLength, 0, 0), new THREE.Vector3(0, 0, 0)]} color={axisColorX} lineWidth={axisLineWidth} dashSize={0.15} gapSize={0.05} opacity={0.4} />

            <DashedLine points={[new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, axisLength, 0)]} color={axisColorY} lineWidth={axisLineWidth} dashSize={0.15} gapSize={0.05} />

            <DashedLine points={[new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, axisLength)]} color={axisColorZ} lineWidth={axisLineWidth} dashSize={0.15} gapSize={0.05} />
            <DashedLine points={[new THREE.Vector3(0, 0, -axisLength), new THREE.Vector3(0, 0, 0)]} color={axisColorZ} lineWidth={axisLineWidth} dashSize={0.15} gapSize={0.05} opacity={0.4} />

            <AxisLabel position={[axisLength + 0.3, 0.2, 0]} text="Forward" color={axisColorX} scale={0.7} />
            <AxisLabel position={[-axisLength - 0.3, 0.2, 0]} text="Backward" color={axisColorX} scale={0.7} />
            <AxisLabel position={[0, 0.2, axisLength + 0.3]} text="Right" color={axisColorZ} scale={0.7} />
            <AxisLabel position={[0, 0.2, -axisLength - 0.3]} text="Left" color={axisColorZ} scale={0.7} />

            {/* Cargo Box with higher transparency for better visibility of internal CoG lines */}
            <CargoBox dimensions={dimensions} theme={theme} opacity={0.15} />

            <mesh position={center}>
                <sphereGeometry args={[0.11, 32, 32]} />
                <meshStandardMaterial color={cogColor} emissive={cogColor} emissiveIntensity={1.0} />
            </mesh>
            <AxisLabel position={[center[0], center[1] + 0.3, center[2]]} text="CoG" color={cogColor} scale={0.7} />

            <mesh position={baseCenter}>
                <sphereGeometry args={[0.05]} />
                <meshBasicMaterial color="#475569" />
            </mesh>

            <ThickLine points={[new THREE.Vector3(...baseCenter), new THREE.Vector3(...bluePoint)]} color="#60a5fa" width={projectionLineWidth} />
            <Label position={bluePoint} color="#60a5fa" />

            <ThickLine points={[new THREE.Vector3(...baseCenter), new THREE.Vector3(...greenPoint)]} color="#f97316" width={projectionLineWidth} />
            <Label position={greenPoint} color="#f97316" />

            <ThickLine points={[new THREE.Vector3(...baseCenter), new THREE.Vector3(...center)]} color="#4ade80" width={projectionLineWidth} />
            <Label position={center} color="#4ade80" />
        </group>
    );
};

const CogScene: React.FC<CogSceneProps> = ({ dimensions, cog, theme = 'dark', zoom = 1 }) => {
    const isDark = theme === 'dark';

    return (
        <Canvas shadows camera={{ position: [8.5, 5.0, 10.5], fov: 38 }} gl={{ alpha: false, powerPreference: 'high-performance' }}>
            <color attach="background" args={[isDark ? '#0D111C' : '#f8fafc']} />
            <ambientLight intensity={isDark ? 0.45 : 0.8} />
            <directionalLight
                position={[5, 10, 5]}
                intensity={isDark ? 0.75 : 1.1}
                color={isDark ? '#e5e7eb' : '#ffffff'}
            />
            <CameraController zoom={zoom} />
            <CogPrism dimensions={dimensions} cog={cog} theme={theme} />

            {/* Standard Grid Floor (Reverted) */}
            <gridHelper
                args={[20, 20, isDark ? '#334155' : '#cbd5e1', isDark ? '#1e293b' : '#e2e8f0']}
                position={[0, -dimensions.height / 2, 0]}
            />
        </Canvas>
    );
};

export default CogScene;
